import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { MlMatch, PaginationMeta, Property, SlipMetadata, PaymentDetail, PaymentParking } from '@/types'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'
import { invoiceKeys } from './useInvoices'
import { closingPackageKeys } from './useClosingPackage'

export interface PaymentListItem {
  id: number
  invoice_id: number
  invoice_number: string | null
  amount: number
  payment_method: string
  paid_at: string
  reference: string | null
  slip_url: string | null
  payment_type?: 'manual' | 'online'
  confirmed_at?: string | null
  verification_status?: string
  slip_metadata?: SlipMetadata
  // List payload keeps slip_metadata out for weight; these two carry what the
  // list page needs: any-slip-present (covers slip_image_key parking slips
  // whose slip_url is nil) + the ML match triage chip (outcome of the gate
  // ladder run — shown as a review-state chip for fraud-suspect / disagreement).
  has_slip?: boolean
  ml_match?: MlMatch | null
  trans_ref?: string
  image_urls?: string[]
  customer: { id: number; name: string; phone?: string } | null
  unit: { id: number; name: string } | null
  parking?: PaymentParking | null
  property?: Pick<Property, 'id' | 'name' | 'address' | 'tax_id' | 'phone' | 'email' | 'payment_methods'> | null
  invoice_items?: { id: number; item_type: string; description: string; quantity: number; unit_price: number; amount: number }[]
  invoice_total?: number
  invoice_due_at?: string
  invoice_billed_at?: string
  created_at: string
}

export const paymentKeys = {
  all: ['payments'] as const,
  lists: () => [...paymentKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...paymentKeys.lists(), filters] as const,
  detail: (id: number) => [...paymentKeys.all, 'detail', id] as const,
}

// EP-97: the index defaults to the latest payment period that has rows and sorts
// by paid_at descending (most recent first) unless the caller overrides.
// `opts.allPeriods` sends `all_periods=true` to escape the period default;
// `opts.sort` / `opts.sort_order` drive the user-selectable server-side column sort.
export function usePayments(
  page = 1,
  method?: string,
  from?: string,
  to?: string,
  propertyId?: number,
  q?: string,
  verificationStatus?: string,
  opts: { sort?: string; sort_order?: string; allPeriods?: boolean; unidentified?: boolean; channel?: string } = {},
) {
  // EP-143 T143-014: 'fraud_suspect' is a special sub-filter value that sends
  // `fraud_suspect=true&verification_status=pending` to the backend rather than
  // a `verification_status=fraud_suspect` (which is not a real status).
  const isFraudSuspectFilter = verificationStatus === 'fraud_suspect'

  return useQuery({
    queryKey: paymentKeys.list({ page, method, from, to, propertyId, q, verificationStatus, ...opts }),
    queryFn: async () => {
      const params: Record<string, unknown> = { page }
      if (method) params.method = method
      if (from) params.from = from
      if (to) params.to = to
      if (propertyId) params.property_id = propertyId
      if (q) params.q = q
      if (isFraudSuspectFilter) {
        // Fraud-suspect filter: show pending payments with the fraud_suspect flag.
        params.verification_status = 'pending'
        params.fraud_suspect = true
      } else if (verificationStatus) {
        params.verification_status = verificationStatus
      }
      // T149-074 (MON-19): composes with q and verification_status, same as
      // the backend's apply_payment_filters (Payment.unidentified.non_parking).
      if (opts.unidentified) params.unidentified = true
      // EP-161 §2.4: parking_kiosk narrows to kiosk parking payments.
      if (opts.channel) params.channel = opts.channel
      if (opts.sort) params.sort = opts.sort
      if (opts.sort_order) params.sort_order = opts.sort_order
      if (opts.allPeriods) params.all_periods = true
      const { data } = await api.get<{ data: PaymentListItem[]; meta: PaginationMeta }>('/api/payments', { params })
      return data
    },
  })
}

export function usePayment(id: number) {
  return useQuery({
    queryKey: paymentKeys.detail(id),
    queryFn: async () => {
      // GET /api/payments/:id renders PaymentBlueprint (the full detail shape),
      // which is richer than the list payload — see PaymentDetail.
      const { data } = await api.get<{ data: PaymentDetail }>(`/api/payments/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

export function useVerifyPayment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      api.post(`/api/payments/${id}/verify`, { reason }),
    onSuccess: (_data, variables) => {
      removeListQueries(queryClient, paymentKeys.all)
      invalidateDetailQueries(queryClient, paymentKeys.detail(variables.id))
    },
  })
}

export function useRejectPayment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      api.post(`/api/payments/${id}/reject`, { reason }),
    onSuccess: (_data, variables) => {
      removeListQueries(queryClient, paymentKeys.all)
      invalidateDetailQueries(queryClient, paymentKeys.detail(variables.id))
    },
  })
}

// EP-101 label capture: persist operator corrections of the ML slip reading
// (training labels — never touches verification_status). Only non-empty
// fields should be sent; the server merges per-field across submissions.
export function useSaveSlipCorrections() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, corrections }: { id: number; corrections: Record<string, string> }) =>
      api.post(`/api/payments/${id}/slip_corrections`, { corrections }),
    onSuccess: (_data, variables) => {
      invalidateDetailQueries(queryClient, paymentKeys.detail(variables.id))
    },
  })
}

export function useRetryVerification() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post(`/api/payments/${id}/retry_verification`, {}),
    onSuccess: (_data, id) => {
      removeListQueries(queryClient, paymentKeys.all)
      invalidateDetailQueries(queryClient, paymentKeys.detail(id))
    },
  })
}

// T149-074 (MON-19 C): attach a customer, and optionally the invoice it
// paid, to a previously unidentified standalone payment. invoice_id present
// posts a JE-PAYID entry server-side and recomputes that invoice's
// amount_paid, so both the payments list/detail AND the invoice tree need
// invalidating; the closing package's payments_unidentified_count also
// drops by one, so its whole cache is invalidated too (no per-month key to
// target from here).
export interface IdentifyPayerInput {
  id: number
  customer_id: number
  invoice_id?: number
}

export function useIdentifyPayer() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...input }: IdentifyPayerInput) => {
      const { data } = await api.post<{ data: PaymentDetail }>(`/api/payments/${id}/identify_payer`, input)
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, paymentKeys.all)
      invalidateDetailQueries(queryClient, paymentKeys.detail(data.id))
      if (data.invoice_id) {
        invalidateDetailQueries(queryClient, invoiceKeys.detail(data.invoice_id))
        removeListQueries(queryClient, invoiceKeys.lists())
      }
      queryClient.invalidateQueries({ queryKey: closingPackageKeys.all })
    },
  })
}

// EP-143 T143-013 — Flag a payment slip as fraud (staff+). Writes fraud
// registry entries for the slip's sender fields and image hash, then sets
// slip_metadata.fraud_suspect = true. Returns 409 if already flagged.
// T143-016: also invalidates recon_candidates so the row disappears from the
// Slip recon tab immediately after flagging.
export function useFlagFraud() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post(`/api/payments/${id}/flag_fraud`, {}),
    onSuccess: (_data, id) => {
      removeListQueries(queryClient, paymentKeys.all)
      invalidateDetailQueries(queryClient, paymentKeys.detail(id))
      // Recon-candidates list must reflect that the payment is now fraud-flagged.
      queryClient.invalidateQueries({ queryKey: [...paymentKeys.all, 'recon_candidates'] })
    },
  })
}

// EP-143 T143-016: shape returned by GET /api/payments/recon_candidates.
// Admin+ only (enforced server-side); accountant gets 403.
export interface ReconCandidate {
  id: number
  amount: number
  payment_method: string
  paid_at: string
  created_at: string
  slip_uploaded_at: string | null
  verification_status: string
  invoice_number: string | null
  trans_ref: string | null
  ml_match: import('@/types').MlMatch | null
  recon_candidate: boolean | null
  customer: { id: number; name: string; phone?: string } | null
}

export function useReconCandidates() {
  return useQuery({
    queryKey: [...paymentKeys.all, 'recon_candidates'] as const,
    queryFn: async () => {
      const { data } = await api.get<{ data: ReconCandidate[] }>('/api/payments/recon_candidates')
      return data.data
    },
    staleTime: 60_000, // 1 minute — matches global default
  })
}

export interface UpdatePaymentInput {
  id: number
  amount?: number
  payment_method?: string
  paid_at?: string
  reference?: string
  // Settlement date (nullable to allow clearing). Editable even on a verified
  // payment — it is not amount/method-locked.
  confirmed_at?: string | null
}

// EP-82: edit a payment from the payments page (PATCH /api/payments/:id).
// Invalidates BOTH payment key trees — this file's top-level lists/detail AND
// useInvoices' invoice-nested payment lists (same ['payments'] root, so
// removeListQueries(paymentKeys.all) clears both) — plus invoiceKeys, since
// editing a payment changes the invoice's amount_paid / balance / status.
export function useUpdatePayment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdatePaymentInput) => {
      const { data } = await api.patch<{ data: PaymentListItem }>(`/api/payments/${id}`, { payment: input })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, paymentKeys.all)
      invalidateDetailQueries(queryClient, paymentKeys.detail(data.id))
      invalidateDetailQueries(queryClient, invoiceKeys.detail(data.invoice_id))
      removeListQueries(queryClient, invoiceKeys.lists())
    },
  })
}

// P005: record a standalone payment (no invoice required). Accepts a customer_id
// or lease_id for account-scope resolution. Invalidates the payment list only
// (no invoice list to invalidate since there is no invoice).
export interface StandalonePaymentInput {
  customer_id?: number
  lease_id?: number
  // EP-104 F2: standalone payments own a property (required by the QuickPay form).
  property_id: number
  amount: number
  payment_method: string
  paid_at: string
  reference?: string
  // T149-070/T149-073 (MON-19 A): lets customer_id stay unset. A
  // request-only flag, never a Payment column server-side.
  unknown_payer?: boolean
}

export function useCreateStandalonePayment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: StandalonePaymentInput) => {
      const { data } = await api.post<{ data: PaymentListItem }>('/api/payments', { payment: input })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, paymentKeys.lists())
    },
  })
}

// EP-82: delete a payment from the payment detail page. Unlike useDeletePayment
// (useInvoices.ts, invoice-context only), this also clears the top-level payment
// list + detail so the /payments list reflects the deletion.
export function useDeletePaymentFromList() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, invoiceId }: { id: number; invoiceId: number }) => {
      await api.delete(`/api/payments/${id}`)
      return { id, invoiceId }
    },
    onSuccess: ({ id, invoiceId }) => {
      removeListQueries(queryClient, paymentKeys.all)
      queryClient.removeQueries({ queryKey: paymentKeys.detail(id) })
      invalidateDetailQueries(queryClient, invoiceKeys.detail(invoiceId))
      removeListQueries(queryClient, invoiceKeys.lists())
    },
  })
}
